import React, { useEffect, useState } from "react";
import {
  TrendingUp,
  Award,
  ShieldCheck,
  Clock,
  Sparkles,
  Heart,
} from "lucide-react";

// RotatingMaureenQuote — six proof-points pulled directly from the
// Maureen Kelley (Nelson County, VA) recommendation letter. Cycles every
// 6.5s with a soft cross-fade. Pauses while hovered. Each "slide" has its
// own icon, headline, sub-headline, and quote so the LEFT and RIGHT halves
// of the bar stay in sync as the rotation advances.

const SLIDES = [
  {
    icon: TrendingUp,
    headline: "Highest in the Commonwealth",
    subhead: "Smithsonian exhibit visitation, Nelson County, VA",
    quote:
      "The Smithsonian staff noted that it was one of the best campaigns they have seen nationwide.",
  },
  {
    icon: Award,
    headline: "10 / 10 Performance Rating",
    subhead: "Director of Tourism · Nelson County, VA",
    quote:
      "Anne Flinn Advertising consistently delivers high-quality creative products, strategic guidance, and measurable results.",
  },
  {
    icon: ShieldCheck,
    headline: "Public-Sector Expertise",
    subhead: "Multi-year DMO partner · Nelson County, VA",
    quote:
      "Particularly effective at working with public-sector organizations, navigating stakeholder considerations, and developing messaging that balances policy objectives with public understanding.",
  },
  {
    icon: Clock,
    headline: "On Time, Every Time",
    subhead: "Director of Tourism · Nelson County, VA",
    quote:
      "Consistently met project deadlines and often exceeds expectations when working under compressed timelines.",
  },
  {
    icon: Sparkles,
    headline: "Strategic Storytelling",
    subhead: "Director of Tourism · Nelson County, VA",
    quote:
      "Translate complex issues into clear and compelling messages, and develop communication strategies that resonate with diverse audiences.",
  },
  {
    icon: Heart,
    headline: "Would Hire Again",
    subhead: "Director of Tourism · Nelson County, VA",
    quote:
      "Absolutely. I would not hesitate to hire them again and would strongly recommend them to other organizations.",
  },
];

const ROTATE_MS = 4000;

const RotatingMaureenQuote = () => {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => {
      setI((prev) => (prev + 1) % SLIDES.length);
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [paused]);

  const slide = SLIDES[i];
  const Icon = slide.icon;

  return (
    <section
      className="border-y border-[var(--af-line)] bg-[var(--af-navy-deep)]"
      data-testid="maureen-rotating-bar"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 grid md:grid-cols-3 gap-6 items-center">
        <div className="flex items-center gap-3 min-h-[64px]">
          <Icon
            size={24}
            className="text-[var(--af-lime)] flex-shrink-0 transition-opacity duration-500"
            key={`icon-${i}`}
          />
          <div
            key={`head-${i}`}
            className="rmq-fade"
            data-testid={`maureen-slide-headline-${i}`}
          >
            <div className="font-display text-2xl leading-tight">
              {slide.headline}
            </div>
            <div className="text-xs text-[var(--af-cream)]/65 mt-1">
              {slide.subhead}
            </div>
          </div>
        </div>

        <div
          className="md:col-span-2 text-sm text-[var(--af-cream)]/80 italic leading-relaxed min-h-[64px]"
          key={`q-${i}`}
          data-testid={`maureen-slide-quote-${i}`}
        >
          <div className="rmq-fade">
            “{slide.quote}”
            <span className="block mt-2 not-italic text-xs text-[var(--af-cream)]/55 font-eyebrow uppercase tracking-wider">
              Maureen Kelley, Nelson County Tourism
            </span>
          </div>
        </div>
      </div>

      {/* Indicator dots */}
      <div
        className="flex items-center justify-center gap-2 pb-5 -mt-2"
        data-testid="maureen-dots"
      >
        {SLIDES.map((_, idx) => (
          <button
            key={idx}
            type="button"
            aria-label={`Show quote ${idx + 1} of ${SLIDES.length}`}
            onClick={() => setI(idx)}
            className={`h-1.5 rounded-full transition-all ${
              idx === i
                ? "w-6 bg-[var(--af-lime)]"
                : "w-1.5 bg-[var(--af-cream)]/30 hover:bg-[var(--af-cream)]/50"
            }`}
            data-testid={`maureen-dot-${idx}`}
          />
        ))}
      </div>

      <style>{`
        .rmq-fade { animation: rmqFade 500ms ease both; }
        @keyframes rmqFade {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .rmq-fade { animation: none; }
        }
      `}</style>
    </section>
  );
};

export default RotatingMaureenQuote;
